import { createStore } from 'zustand/vanilla'
import { useSyncExternalStore } from 'react'
import {
  DEFAULT_THEME,
  THEMES,
  THEME_STORAGE_KEY,
  migrateStoredTheme,
  themeForMode,
  toggledMode,
  type ThemeId,
  type ThemeMode,
} from './themes'

/**
 * The current theme: Signal, dark or light.
 *
 * With nothing stored the portal matches the operating system and follows it as it changes. Picking
 * Light or Dark stores that choice under `relay-theme` and stops following; "Match system" clears it.
 * The theme is applied by setting `data-theme` on <html>.
 *
 * A zustand *vanilla* store, not the React-bound one. Under Module Federation the React-bound
 * store pulls `useSyncExternalStore` through its own import graph, which can drag in a second
 * React copy; the platform's other modules hit this and settled on vanilla stores read through
 * `getState()`. The `useTheme` hook below subscribes explicitly instead.
 */

interface ThemeState {
  theme: ThemeId
  /** True when no explicit choice is stored and the theme tracks the operating system. */
  followsSystem: boolean
  setTheme: (theme: ThemeId) => void
  toggleMode: () => void
  setMode: (mode: ThemeMode) => void
  followSystem: () => void
}

const LIGHT_QUERY = '(prefers-color-scheme: light)'

/** The theme the operating system asks for. Dark when it expresses no preference. */
export function systemTheme(): ThemeId {
  try {
    if (typeof window !== 'undefined' && window.matchMedia?.(LIGHT_QUERY).matches) {
      return themeForMode('light')
    }
  } catch {
    /* No matchMedia (some test environments): fall through to the default. */
  }
  return DEFAULT_THEME
}

/** The explicit choice in storage, if any. Storage can throw (private mode) — never fatal. */
function storedChoice(): ThemeId | null {
  try {
    return migrateStoredTheme(localStorage.getItem(THEME_STORAGE_KEY))
  } catch {
    return null
  }
}

/** The theme to show: the stored choice, else the system's. */
export function readStoredTheme(): ThemeId {
  return storedChoice() ?? systemTheme()
}

function persist(theme: ThemeId | null): void {
  try {
    if (theme) localStorage.setItem(THEME_STORAGE_KEY, theme)
    else localStorage.removeItem(THEME_STORAGE_KEY)
  } catch {
    /* Preference is not persisted this session; the applied theme is still correct. */
  }
}

/** Applies the theme to the document. index.html does this before first paint to avoid a flash. */
export function applyTheme(theme: ThemeId): void {
  if (typeof document === 'undefined') return
  document.documentElement.setAttribute('data-theme', theme)
}

export const themeStore = createStore<ThemeState>((set, get) => ({
  theme: readStoredTheme(),
  followsSystem: storedChoice() === null,

  setTheme: (theme) => {
    applyTheme(theme)
    persist(theme)
    set({ theme, followsSystem: false })
  },

  toggleMode: () => get().setTheme(toggledMode(get().theme)),
  setMode: (mode) => get().setTheme(themeForMode(mode)),

  followSystem: () => {
    persist(null)
    const theme = systemTheme()
    applyTheme(theme)
    set({ theme, followsSystem: true })
  },
}))

let watchingSystem = false

/** Re-applies the system's theme when it changes, for as long as no explicit choice is stored. */
function watchSystem(): void {
  if (watchingSystem || typeof window === 'undefined' || !window.matchMedia) return
  watchingSystem = true
  window.matchMedia(LIGHT_QUERY).addEventListener?.('change', () => {
    if (!themeStore.getState().followsSystem) return
    const theme = systemTheme()
    applyTheme(theme)
    themeStore.setState({ theme })
  })
}

/**
 * Sync the store and the document to the persisted theme at startup.
 *
 * index.html already set the attribute before first paint; this keeps the store and the DOM in
 * agreement when the app is mounted somewhere that inline script did not run — notably inside the
 * AboveStore shell, whose index.html is not ours.
 *
 * Storage is re-read here rather than trusting the store's initial value, which was captured when
 * this module was first evaluated. Under Module Federation those are not the same moment: the
 * shell can fetch and evaluate a remote's entry long before the user navigates to it, and the
 * theme may have changed in between — in another tab, or in the shell itself. Re-reading makes
 * mount time authoritative, which is the moment that actually matters.
 */
export function initTheme(): ThemeId {
  const choice = storedChoice()
  const theme = choice ?? systemTheme()
  applyTheme(theme)
  // A legacy id (technical-light) is rewritten as its current equivalent the first time it is read.
  if (choice) persist(choice)
  themeStore.setState({ theme, followsSystem: choice === null })
  watchSystem()
  return theme
}

/** Subscribe a component to the current theme. */
export function useTheme(): ThemeId {
  return useSyncExternalStore(
    themeStore.subscribe,
    () => themeStore.getState().theme,
    () => DEFAULT_THEME,
  )
}

/** Whether the theme is following the operating system rather than an explicit choice. */
export function useFollowsSystem(): boolean {
  return useSyncExternalStore(
    themeStore.subscribe,
    () => themeStore.getState().followsSystem,
    () => true,
  )
}

/** The current theme's metadata — its mode and that mode's display name. */
export function useThemeInfo() {
  return THEMES[useTheme()]
}

export const themeActions = {
  setTheme: (theme: ThemeId) => themeStore.getState().setTheme(theme),
  toggleMode: () => themeStore.getState().toggleMode(),
  setMode: (mode: ThemeMode) => themeStore.getState().setMode(mode),
  followSystem: () => themeStore.getState().followSystem(),
}
